import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import type { ThemeDto } from '../api/themes.api';
import { useGetThemeForClientQuery, useRemoveThemeMutation, useSaveThemeMutation } from '../api/themes.api';
import { useThemeEditorContext } from '../context/ThemeEditorContext';
import { isValidHex, optimizeImage } from '../utils/themeUtils';
import { useUser } from 'hooks/useUser';
import { useGetAllClientsQuery, useGetClientByIdQuery } from 'rtk/query/apis/clients';
import { useFindPaginatedJobsQuery } from 'rtk/query/apis/jobs';
import { useGetOutreachCampaignsQuery } from 'rtk/query/apis/outreach';
import { useFindTemplatesQuery } from 'rtk/query/apis/templates';
import { defaultBrandingPrimaryColor, defaultBrandingTextColor } from 'types/branding.types';
import { isRecruiterUser } from 'types/user.types';

export const useThemeEditor = () => {
  const user = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { themeOverrides, setThemeOverrides } = useThemeEditorContext();

  const activeThemeId = params.themeId;
  const urlClientId = searchParams.get('clientId');

  const clientId = useMemo(() => {
    if (isRecruiterUser(user)) return user.client_id;
    return urlClientId || '';
  }, [user, urlClientId]);

  const { data: clientsData, isLoading: isLoadingClients } = useGetAllClientsQuery(undefined);
  const {
    data: clientData,
    isLoading: isLoadingClient,
    isFetching: isFetchingClient,
    refetch: refetchClient,
  } = useGetClientByIdQuery(clientId, { skip: !clientId });
  const {
    data: themesData,
    isLoading: isLoadingThemes,
    isFetching: isFetchingThemes,
    refetch: refetchThemes,
  } = useGetThemeForClientQuery(clientId || '', {
    skip: !clientId,
  });

  const {
    data: templatesData,
    isLoading: isLoadingTemplates,
    refetch: refetchTemplates,
  } = useFindTemplatesQuery(
    { filter: { clientId: clientId || '', context: 'current', name: '', visibility: [] } },
    { skip: !clientId, refetchOnMountOrArgChange: true },
  );

  const {
    data: campaignsData,
    isLoading: isLoadingCampaigns,
    refetch: refetchCampaigns,
  } = useGetOutreachCampaignsQuery(clientId || '', { skip: !clientId, refetchOnMountOrArgChange: true });

  const {
    data: paginatedJobsData,
    isLoading: isLoadingJobs,
    refetch: refetchJobs,
  } = useFindPaginatedJobsQuery(
    { pageIndex: 0, pageSize: 10000, filter: { clientId: clientId || '', text: '', myJobs: false } },
    { skip: !clientId, refetchOnMountOrArgChange: true },
  );

  const [removeTheme] = useRemoveThemeMutation();
  const [saveThemeMutation, { isLoading: isSaving }] = useSaveThemeMutation();

  const sortedClients = useMemo(() => {
    if (!clientsData?.clients) return [];
    return [...clientsData.clients].sort((a, b) => a.org_name.localeCompare(b.org_name));
  }, [clientsData]);

  const allThemes = useMemo(() => {
    const themesMap = new Map<string, ThemeDto>();

    // 1. Set Hubert Default
    themesMap.set('hubert-default', {
      id: 'hubert-default',
      level: 'basic',
      name: 'Hubert',
      agentName: 'Hubert',
      policies: [{ url: 'https://www.hubert.ai/candidate-privacy-policy' }],
      colorScheme: 'light',
      brandColor: defaultBrandingPrimaryColor,
      textColor: defaultBrandingTextColor,
      showOverviewLogo: true,
      clientId,
      applyToClient: !clientData?.client.theme_id || clientData?.client.theme_id === 'hubert-default',
    });

    // 2. Add themes from API
    themesData?.forEach((t) => {
      themesMap.set(t.id!, {
        ...t,
        clientId: t.clientId || clientId,
        applyToClient: clientData?.client.theme_id === t.id,
      });
    });

    // 3. Layer on all overrides (handles modified existing and new temp themes)
    Object.entries(themeOverrides).forEach(([id, override]) => {
      const existing = themesMap.get(id);
      themesMap.set(id, existing ? { ...existing, ...override } : override);
    });

    const list = Array.from(themesMap.values());

    // 4. Determine final applied theme (Reactive Fallback)
    const appliedId = list.find((t) => t.id !== 'hubert-default' && t.applyToClient === true)?.id || 'hubert-default';

    return list.map((t) => ({
      ...t,
      applyToClient: t.id === appliedId,
    }));
  }, [themesData, themeOverrides, clientData, clientId]);

  const activeTheme = useMemo(() => {
    if (activeThemeId) {
      return allThemes.find((t) => t.id === activeThemeId) || allThemes[0];
    }
    return allThemes[0];
  }, [allThemes, activeThemeId]);

  useEffect(() => {
    // REDIRECT FIX: Wait for ALL loading and fetching states to be false
    // before validating the route. This prevents incorrect redirects to
    // hubert-default on refresh or after saving a new theme.
    const isStable =
      !!themesData && !isLoadingThemes && !isFetchingThemes && !isLoadingClient && !isFetchingClient && !isSaving;

    if (isStable && activeThemeId && activeThemeId !== 'hubert-default') {
      const exists = allThemes.some((t) => t.id === activeThemeId);
      if (!exists) {
        navigate({ pathname: `/themes/hubert-default`, search: searchParams.toString() }, { replace: true });
      }
    }
  }, [
    activeThemeId,
    allThemes,
    navigate,
    searchParams,
    isLoadingThemes,
    isFetchingThemes,
    isLoadingClient,
    isFetchingClient,
    isSaving,
    themesData,
  ]);

  const addTheme = () => {
    const hubertTheme = allThemes.find((t) => t.id === 'hubert-default');
    const newId = `temp-${Math.random().toString(36).substring(2, 12)}`;
    const newTheme: ThemeDto = {
      ...hubertTheme,
      policies: undefined,
      id: newId,
      name: 'New Theme',
      clientId,
      applyToClient: false,
    };

    setThemeOverrides((prev) => ({
      ...prev,
      [newId]: newTheme,
    }));

    navigate({ pathname: `/themes/${newId}`, search: searchParams.toString() });
  };

  const deleteTheme = async (themeId: string) => {
    if (themeId === 'hubert-default') return;

    try {
      if (!themeId.startsWith('temp-')) {
        await removeTheme(themeId).unwrap();
        toast.success('Theme removed successfully');
      }

      setThemeOverrides((prev) => {
        const next = { ...prev };
        delete next[themeId];
        return next;
      });

      if (activeThemeId === themeId) {
        navigate({ pathname: '/themes/hubert-default', search: searchParams.toString() });
      }
    } catch (err) {
      console.error('Failed to remove theme:', err);
      toast.error('Failed to remove theme. Please try again.');
    }
  };

  const updateTheme = (updates: Partial<ThemeDto>) => {
    if (!activeThemeId) return;
    setThemeOverrides((prev) => ({
      ...prev,
      [activeThemeId]: { ...(prev[activeThemeId] || activeTheme), ...updates },
    }));
  };

  const saveTheme = async () => {
    if (activeTheme.id === 'hubert-default' || !activeTheme.id) return;

    if (!isValidHex(activeTheme.brandColor || '')) {
      toast.error('Please enter a valid hex color (e.g. #794dff)');
      return;
    }

    try {
      const result = await saveThemeMutation(activeTheme).unwrap();
      const isNew = activeTheme.id.startsWith('temp-');
      const savedId = isNew ? result.id : activeTheme.id;

      if (isNew) {
        // If it's a new theme, navigate to its permanent ID.
        // The isStable guard in useEffect will prevent a redirect
        // while the background refetches (triggered by invalidatesTags) are running.
        navigate({ pathname: `/themes/${savedId}`, search: searchParams.toString() }, { replace: true });
      }

      // Explicitly await refetches to keep isFetching=true for longer
      await Promise.all([refetchThemes(), refetchClient(), refetchTemplates(), refetchCampaigns(), refetchJobs()]);

      toast.success('Theme saved successfully');

      // Clear overrides for this theme as it's now fully synchronized with the server
      setThemeOverrides((prev) => {
        const next = { ...prev };
        delete next[activeTheme.id!];
        delete next[savedId];
        return next;
      });
    } catch (err) {
      console.error('Failed to save theme:', err);
      toast.error('Failed to save theme. Please try again.');
    }
  };

  const uploadImage = async (file: File, field: 'logo' | 'favicon') => {
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid file type. Please upload an image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large. Max 10MB.');
      return;
    }
    try {
      const optimizedBase64 = await optimizeImage(file);
      updateTheme({ [field]: { light: optimizedBase64 } });
    } catch (err) {
      console.error('Image optimization failed:', err);
      toast.error('Failed to process image. Please try again.');
    }
  };

  const uploadLogo = async (file: File) => uploadImage(file, 'logo');

  const uploadFavicon = async (file: File) => uploadImage(file, 'favicon');

  return {
    user,
    clientId,
    activeThemeId,
    activeTheme,
    allThemes,
    themeOverrides,
    isLoadingClients,
    isLoadingThemes,
    isSaving,
    sortedClients,
    templatesData,
    campaignsData,
    paginatedJobsData,
    isLoadingTemplates,
    isLoadingCampaigns,
    isLoadingJobs,
    addTheme,
    deleteTheme,
    updateTheme,
    saveTheme,
    uploadLogo,
    uploadFavicon,
    searchParams,
    navigate,
  };
};
